import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/core/AuthContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export function ClientMagicLogin() {
  const { token } = useParams<{ token: string }>();
  const { signInAsClient, userData } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const handleClientLogin = useCallback(
    async (secretToken: string) => {
      try {
        await signInAsClient(secretToken);
      } catch (err: unknown) {
        console.error(err);
        setError('Invalid client link. Ask your trainer for a new one.');
        setLoading(false);
      }
    },
    [signInAsClient]
  );

  useEffect(() => {
    if (userData?.role === 'client') {
      navigate('/client');
      return;
    }

    if (token) {
      handleClientLogin(token);
    } else {
      setError('Invalid client link');
      setLoading(false);
    }
  }, [handleClientLogin, navigate, token, userData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-400">Signing you in...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full"
        >
          <Card variant="elevated" padding="lg" className="shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-red-600 dark:text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Access Denied</h2>
              <p className="text-gray-600 dark:text-slate-400 mb-6">{error}</p>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return null;
}
